import React, { useEffect, useState } from "react";
import PageTitle from "../../PageTitle";
import useAxios from "../../../utilities/useAxios";
import {
	Button,
	Card,
	ConfigProvider,
	DatePicker,
	Form,
	Input,
	Modal,
	Select,
	Space,
	Table,
	Tag,
	TimePicker,
	Typography,
	message,
} from "antd";
import {
	DeleteOutlined,
	EditOutlined,
	SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";
import useIsMobile from "../../../hooks/useIsMobile";
import PageHeader from "../../common/PageHeader";

dayjs.locale("ko");

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const SANCTUARY_OTHER_VALUE = "__OTHER__";

const statusColor = {
	신청: "processing",
	승인: "success",
	거절: "error",
};

const LookupRentSanctuaryApplications = () => {
	const isMobile = useIsMobile();
	const [lookupForm] = Form.useForm();
	const [editForm] = Form.useForm();
	const [identity, setIdentity] = useState(null);
	const [applications, setApplications] = useState([]);
	const [loading, setLoading] = useState(false);
	const [lookedUp, setLookedUp] = useState(false);
	const [sanctuaryRoomList, setSanctuaryRoomList] = useState([]);
	const [editModalVisible, setEditModalVisible] = useState(false);
	const [selectedApplication, setSelectedApplication] = useState(null);
	const editSanctuaryChoice = Form.useWatch("sanctuary_room", editForm);

	useEffect(() => {
		loadSanctuaryRooms();
	}, []);

	const loadSanctuaryRooms = async () => {
		try {
			const response = await useAxios.get("/utils/sanctuary/list/");
			if (response.status === 200) {
				const list = [...response.data].sort((a, b) => {
					if (a.sanctuary_name === b.sanctuary_name) {
						return a.room_name.localeCompare(b.room_name);
					}
					return a.sanctuary_name.localeCompare(
						b.sanctuary_name,
					);
				});
				setSanctuaryRoomList(list);
			}
		} catch {
			message.error("성전 목록을 불러오는데 실패했습니다.");
		}
	};

	const fetchApplications = async (rep, tel) => {
		setLoading(true);
		try {
			const response = await useAxios.post(
				"/utils/rental/sanctuary/applicant-lookup/",
				{
					representative: rep,
					representative_tel: tel,
				},
			);
			if (response.status === 200) {
				setApplications(response.data || []);
				setIdentity({
					representative: rep,
					representative_tel: tel,
				});
				setLookedUp(true);
				if (!(response.data || []).length) {
					message.info("일치하는 신청 내역이 없습니다.");
				}
			}
		} catch (error) {
			message.error(
				error.response?.data?.error ||
					"신청 내역을 조회하지 못했습니다.",
			);
		} finally {
			setLoading(false);
		}
	};

	const handleLookup = async () => {
		try {
			const values = await lookupForm.validateFields();
			await fetchApplications(
				values.representative.trim(),
				values.representative_tel.trim(),
			);
		} catch (error) {
			if (error?.errorFields) {
				return;
			}
		}
	};

	const sanctuaryLabel = (record) => {
		if (record.sanctuary_room) {
			const name =
				record.sanctuary_name ||
				record.sanctuary_room.sanctuary_name ||
				"";
			const room = record.sanctuary_room.room_name;
			if (!room || room === "-") return name;
			return `${name} ${room}`;
		}
		return (
			record.sanctuary_name ||
			record.custom_sanctuary_name ||
			"기타"
		);
	};

	const openEdit = (record) => {
		setSelectedApplication(record);
		const startDateTime = dayjs(record.start_datetime);
		const endDateTime = dayjs(record.end_datetime);
		editForm.setFieldsValue({
			sanctuary_room:
				record.sanctuary_room?.id ??
				(record.custom_sanctuary_name
					? SANCTUARY_OTHER_VALUE
					: undefined),
			custom_sanctuary_name: record.sanctuary_room
				? ""
				: record.custom_sanctuary_name || "",
			date: startDateTime,
			startTime: startDateTime,
			endTime: endDateTime,
			rent_team: record.rent_team,
			rent_for: record.rent_for,
			representative: record.representative,
			representative_tel: record.representative_tel,
		});
		setEditModalVisible(true);
	};

	const handleEditConfirm = async () => {
		if (!identity || !selectedApplication) return;
		try {
			const values = await editForm.validateFields();
			if (values.endTime <= values.startTime) {
				message.error(
					"종료 시간은 시작 시간보다 크게 설정해주세요.",
				);
				return;
			}

			setLoading(true);
			const startDateTime = dayjs(values.date)
				.hour(values.startTime.hour())
				.minute(values.startTime.minute());
			const endDateTime = dayjs(values.date)
				.hour(values.endTime.hour())
				.minute(values.endTime.minute());

			let sanctuary_room = values.sanctuary_room;
			let custom_sanctuary_name = "";
			if (sanctuary_room === SANCTUARY_OTHER_VALUE) {
				sanctuary_room = null;
				custom_sanctuary_name = (
					values.custom_sanctuary_name || ""
				).trim();
			}

			const response = await useAxios.put(
				`/utils/rental/sanctuary/applicant-lookup/${selectedApplication.id}/`,
				{
					identity_representative: identity.representative,
					identity_representative_tel:
						identity.representative_tel,
					sanctuary_room,
					custom_sanctuary_name,
					start_datetime: startDateTime.format(
						"YYYY-MM-DD HH:mm:ss",
					),
					end_datetime: endDateTime.format(
						"YYYY-MM-DD HH:mm:ss",
					),
					rent_team: values.rent_team,
					rent_for: values.rent_for,
					representative: values.representative,
					representative_tel: values.representative_tel,
				},
			);

			if (response.status === 200) {
				message.success("신청이 수정되었습니다.");
				setEditModalVisible(false);
				editForm.resetFields();
				const nextIdentity = {
					representative: values.representative.trim(),
					representative_tel:
						values.representative_tel.trim(),
				};
				lookupForm.setFieldsValue(nextIdentity);
				await fetchApplications(
					nextIdentity.representative,
					nextIdentity.representative_tel,
				);
			}
		} catch (error) {
			if (error.errorFields) {
				message.error("입력 정보를 확인해주세요.");
			} else {
				message.error(
					error.response?.data?.error ||
						"신청 수정 중 오류가 발생했습니다.",
				);
			}
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = (record) => {
		if (!identity) return;
		Modal.confirm({
			title: "신청 삭제",
			content: "이 성전 대여 신청을 삭제하시겠습니까?",
			okText: "삭제",
			okType: "danger",
			cancelText: "취소",
			onOk: async () => {
				try {
					setLoading(true);
					await useAxios.delete(
						`/utils/rental/sanctuary/applicant-lookup/${record.id}/`,
						{
							data: {
								representative:
									identity.representative,
								representative_tel:
									identity.representative_tel,
							},
						},
					);
					message.success("신청이 삭제되었습니다.");
					await fetchApplications(
						identity.representative,
						identity.representative_tel,
					);
				} catch (error) {
					message.error(
						error.response?.data?.error ||
							"신청 삭제 중 오류가 발생했습니다.",
					);
				} finally {
					setLoading(false);
				}
			},
		});
	};

	const columns = [
		{
			title: "성전",
			key: "sanctuary",
			width: 160,
			ellipsis: true,
			render: (_, record) => sanctuaryLabel(record),
		},
		{
			title: "대여 일시",
			key: "datetime",
			width: 200,
			render: (_, record) =>
				`${dayjs(record.start_datetime).format("YYYY-MM-DD HH:mm")} ~ ${dayjs(record.end_datetime).format("HH:mm")}`,
		},
		{
			title: "교구/기관",
			dataIndex: "rent_team",
			key: "rent_team",
			width: 120,
			ellipsis: true,
		},
		{
			title: "사용 목적",
			dataIndex: "rent_for",
			key: "rent_for",
			width: 140,
			ellipsis: true,
		},
		{
			title: "상태",
			dataIndex: "status",
			key: "status",
			width: 80,
			render: (status) => (
				<Tag color={statusColor[status] || "default"}>
					{status}
				</Tag>
			),
		},
		{
			title: "작업",
			key: "actions",
			width: 140,
			fixed: "right",
			render: (_, record) => {
				if (record.status !== "신청") {
					return <Text type="secondary">조회만</Text>;
				}
				return (
					<Space size="small">
						<Button
							type="link"
							size="small"
							icon={<EditOutlined />}
							onClick={() => openEdit(record)}>
							수정
						</Button>
						<Button
							type="link"
							size="small"
							danger
							icon={<DeleteOutlined />}
							onClick={() => handleDelete(record)}>
							삭제
						</Button>
					</Space>
				);
			},
		},
	];

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="성전 대여 신청 조회" />
			<div
				className="utils-rent_sanctuary__container page"
				style={{
					maxWidth: 960,
					margin: "0 auto",
					padding: "16px",
				}}>
				<PageHeader
					title="성전 대여 신청 조회"
					subtitle="신청 시 입력한 이름과 연락처로 본인 신청 내역을 확인할 수 있습니다. 신청 상태인 건만 수정·삭제할 수 있습니다."
				/>

				<Card
					className="surface-card"
					style={{ marginTop: 16, marginBottom: 16 }}>
					<Form
						form={lookupForm}
						layout="vertical"
						onFinish={handleLookup}>
						<Form.Item
							name="representative"
							label="신청자 이름"
							rules={[
								{
									required: true,
									message:
										"신청자 이름을 입력해주세요",
								},
							]}>
							<Input placeholder="신청자 이름" />
						</Form.Item>
						<Form.Item
							name="representative_tel"
							label="신청자 핸드폰 번호"
							rules={[
								{
									required: true,
									message:
										"핸드폰 번호를 입력해주세요",
								},
								{
									pattern: /^[0-9-]+$/,
									message:
										"숫자와 하이픈(-)만 입력 가능합니다",
								},
							]}>
							<Input placeholder="예: 010-1234-5678" />
						</Form.Item>
						<Button
							type="primary"
							htmlType="submit"
							icon={<SearchOutlined />}
							loading={loading}
							block>
							조회하기
						</Button>
					</Form>
				</Card>

				{lookedUp && (
					<Card>
						<Table
							size="small"
							rowKey="id"
							loading={loading}
							columns={columns}
							dataSource={applications}
							scroll={{ x: 840 }}
							sticky={{ offsetHeader: 64 }}
							pagination={{
								pageSize: 10,
								showTotal: (t) => `총 ${t}건`,
							}}
							locale={{
								emptyText: "신청 내역이 없습니다.",
							}}
						/>
					</Card>
				)}

				<Modal
					title="신청 수정"
					open={editModalVisible}
					onOk={handleEditConfirm}
					onCancel={() => {
						setEditModalVisible(false);
						editForm.resetFields();
					}}
					okText="수정"
					cancelText="취소"
					width={Math.min(600, window.innerWidth - 32)}
					okButtonProps={{ loading }}
					destroyOnClose>
					<Form form={editForm} layout="vertical">
						<Form.Item
							name="sanctuary_room"
							label="성전 선택"
							rules={[
								{
									required: true,
									message: "성전을 선택해주세요",
								},
							]}>
							<Select placeholder="성전을 선택하세요">
								{sanctuaryRoomList.map(
									(sanctuaryRoom) => (
										<Option
											key={
												sanctuaryRoom.id
											}
											value={
												sanctuaryRoom.id
											}>
											{sanctuaryRoom.room_name ===
											"-"
												? sanctuaryRoom.sanctuary_name
												: `${sanctuaryRoom.sanctuary_name} ${sanctuaryRoom.room_name}`}
										</Option>
									),
								)}
								<Option
									key="__OTHER__"
									value={SANCTUARY_OTHER_VALUE}>
									기타
								</Option>
							</Select>
						</Form.Item>

						{editSanctuaryChoice ===
							SANCTUARY_OTHER_VALUE && (
							<Form.Item
								name="custom_sanctuary_name"
								label="성전 이름"
								rules={[
									{
										required: true,
										message:
											"성전 이름을 입력해주세요",
									},
								]}>
								<Input placeholder="신청할 성전 이름을 입력하세요" />
							</Form.Item>
						)}

						<Form.Item
							name="date"
							label="날짜"
							rules={[
								{
									required: true,
									message: "날짜를 선택해주세요",
								},
							]}>
							<DatePicker
								inputReadOnly={isMobile}
								style={{ width: "100%" }}
								format="YYYY-MM-DD"
								allowClear={false}
							/>
						</Form.Item>

						<Form.Item
							name="startTime"
							label="시작 시간"
							rules={[
								{
									required: true,
									message:
										"시작 시간을 선택해주세요",
								},
							]}>
							<TimePicker
								inputReadOnly={isMobile}
								style={{ width: "100%" }}
								format="HH:mm"
								minuteStep={30}
								allowClear={false}
							/>
						</Form.Item>

						<Form.Item
							name="endTime"
							label="종료 시간"
							rules={[
								{
									required: true,
									message:
										"종료 시간을 선택해주세요",
								},
							]}>
							<TimePicker
								inputReadOnly={isMobile}
								style={{ width: "100%" }}
								format="HH:mm"
								minuteStep={30}
								allowClear={false}
							/>
						</Form.Item>

						<Form.Item
							name="rent_team"
							label="교구/기관명"
							rules={[
								{
									required: true,
									message:
										"교구/기관명을 입력해주세요",
								},
							]}>
							<Input placeholder="교구/기관명을 입력하세요" />
						</Form.Item>

						<Form.Item
							name="rent_for"
							label="사용 목적"
							rules={[
								{
									required: true,
									message:
										"사용 목적을 입력해주세요",
								},
							]}>
							<TextArea
								rows={3}
								placeholder="사용 목적을 입력하세요"
							/>
						</Form.Item>

						<Form.Item
							name="representative"
							label="신청자 이름"
							rules={[
								{
									required: true,
									message:
										"신청자 이름을 입력해주세요",
								},
							]}>
							<Input placeholder="신청자 이름을 입력하세요" />
						</Form.Item>

						<Form.Item
							name="representative_tel"
							label="신청자 연락처"
							rules={[
								{
									required: true,
									message:
										"신청자 연락처를 입력해주세요",
								},
								{
									pattern: /^[0-9-]+$/,
									message:
										"숫자와 하이픈(-)만 입력 가능합니다",
								},
							]}>
							<Input placeholder="예: 010-1234-5678" />
						</Form.Item>
					</Form>
				</Modal>
			</div>
		</ConfigProvider>
	);
};

export default LookupRentSanctuaryApplications;
